import { useState, useEffect, useRef, useCallback } from "react";
import { useHistory } from "react-router-dom";
import openSocket from "../../services/socket-io";

import { toast } from "react-toastify";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { parseJwtPayload, isJwtExpired } from "../../utils/parseJwt";

const useAuth = () => {
  const history = useHistory();
  const [isAuth, setIsAuth] = useState(false);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState({});

  const setIsAuthRef = useRef(setIsAuth);
  const setUserRef = useRef(setUser);
  setIsAuthRef.current = setIsAuth;
  setUserRef.current = setUser;

  useEffect(() => {
    const reqId = api.interceptors.request.use(
      config => {
        const raw = localStorage.getItem("token");
        if (raw) {
          try {
            const token = JSON.parse(raw);
            config.headers.Authorization = `Bearer ${token}`;
          } catch {
            /* ignore */
          }
        }
        return config;
      },
      error => Promise.reject(error)
    );

    const resId = api.interceptors.response.use(
      response => response,
      async error => {
        const originalRequest = error.config;
        if (error?.response?.status === 403 && !originalRequest._retry) {
          originalRequest._retry = true;
          try {
            const { data } = await api.post("/auth/refresh_token");
            if (data?.token) {
              localStorage.setItem("token", JSON.stringify(data.token));
              api.defaults.headers.Authorization = `Bearer ${data.token}`;
              originalRequest.headers.Authorization = `Bearer ${data.token}`;
            }
            return api(originalRequest);
          } catch {
            localStorage.removeItem("token");
            api.defaults.headers.Authorization = undefined;
            setIsAuthRef.current(false);
            return Promise.reject(error);
          }
        }
        if (error?.response?.status === 401) {
          localStorage.removeItem("token");
          api.defaults.headers.Authorization = undefined;
          setIsAuthRef.current(false);
        }
        return Promise.reject(error);
      }
    );

    return () => {
      api.interceptors.request.eject(reqId);
      api.interceptors.response.eject(resId);
    };
  }, []);

  useEffect(() => {
    const bootstrap = async () => {
      const raw = localStorage.getItem("token");
      if (!raw) {
        setLoading(false);
        return;
      }

      let accessToken;
      try {
        accessToken = JSON.parse(raw);
      } catch {
        localStorage.removeItem("token");
        setLoading(false);
        return;
      }

      try {
        const { data } = await api.post("/auth/refresh_token");
        if (data?.token) {
          localStorage.setItem("token", JSON.stringify(data.token));
          api.defaults.headers.Authorization = `Bearer ${data.token}`;
          setUser(data.user);
          setIsAuth(true);
        }
      } catch {
        try {
          if (!isJwtExpired(accessToken)) {
            api.defaults.headers.Authorization = `Bearer ${accessToken}`;
            const payload = parseJwtPayload(accessToken);
            const uid = payload?.id;
            if (uid != null) {
              const { data: profile } = await api.get(`/users/${uid}`);
              setUser(profile);
              setIsAuth(true);
            }
          } else {
            localStorage.removeItem("token");
          }
        } catch {
          localStorage.removeItem("token");
          api.defaults.headers.Authorization = undefined;
          setIsAuth(false);
        }
      }

      setLoading(false);
    };

    bootstrap();
  }, []);

  useEffect(() => {
    if (!user?.id) return undefined;

    const socket = openSocket();

    socket.on("user", data => {
      if (data.action === "update" && data.user?.id === user.id) {
        setUser(data.user);
      }
    });

    return () => {
      socket.disconnect();
    };
  }, [user?.id]);

  const handleLogin = async userData => {
    setLoading(true);

    try {
      const { data } = await api.post("/auth/login", userData);
      localStorage.setItem("token", JSON.stringify(data.token));
      api.defaults.headers.Authorization = `Bearer ${data.token}`;
      setUser(data.user);
      setIsAuth(true);
      toast.success(i18n.t("auth.toasts.success"));
      history.push("/tickets");
      setLoading(false);
    } catch (err) {
      toastError(err);
      setLoading(false);
    }
  };

  const handleLogout = useCallback(async () => {
    setLoading(true);

    try {
      await api.delete("/auth/logout");
      setIsAuth(false);
      setUser({});
      localStorage.removeItem("token");
      api.defaults.headers.Authorization = undefined;
      setLoading(false);
      history.push("/login");
    } catch (err) {
      toastError(err);
      setLoading(false);
    }
  }, [history]);

  return { isAuth, user, loading, handleLogin, handleLogout };
};

export default useAuth;
